import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	InputAdornment,
	LinearProgress,
	MenuItem,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	compactDateSlotProps,
	compactInputSx,
	primaryButtonSx,
	SectionCard,
} from "../sharedStyles";

const DOWNLOAD_REPORTS = [
	{
		key: "discount_working",
		label: "Discount Working",
		description: "CSV to check sale discounts and offer status",
		icon: TableChartOutlinedIcon,
		format: "CSV",
		reportType: "discount_working",
		filePrefix: "mbo_discount_working",
		fileExtension: "csv",
		mimeType: "text/csv;charset=utf-8;",
		successMessage: "Discount working file downloaded",
	},
	{
		key: "calculated_payment_working",
		label: "Calculated Payment Working",
		description: "Excel billing sheet using offer-calculated net",
		icon: CalculateOutlinedIcon,
		format: "Excel",
		reportType: "calculated_payment_working",
		filePrefix: "mbo_calculated_payment_working",
		fileExtension: "xlsx",
		mimeType:
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		successMessage: "Calculated payment working billing sheet downloaded",
	},
];

const DEFAULT_REPORT_KEY = DOWNLOAD_REPORTS[0].key;

// Discount-working (snake_case) and billing-sheet (UPPER CASE) numeric columns.
const TEXT_COLUMNS = new Set([
	"distributors",
	"g_short_name",
	"g_code",
	"store_code",
	"brand",
	"division",
	"department",
	"style",
	"color",
	"season",
	"fabric",
	"hsn_code",
	"billdate",
	"billno",
	"barcode",
	"offer",
	"offer_type",
	"discount_status",
	"discount_comment",
	"DISTRIBUTOR",
	"STORE CODE",
	"G-CODE",
	"STORE NAME",
	"BILLNO",
	"BILLDATE",
	"BARCODE",
	"HSN CODE",
	"BRAND",
	"SESSION",
	"STYLE",
	"DIVISION",
	"OFFER",
]);

const WIDE_COLUMNS = {
	distributors: 200,
	g_short_name: 190,
	discount_comment: 320,
	offer: 240,
	style: 150,
	billno: 150,
	barcode: 140,
	discount_status: 170,
	offer_type: 170,
	DISTRIBUTOR: 220,
	"STORE NAME": 190,
	BILLNO: 150,
	BARCODE: 140,
	STYLE: 150,
	OFFER: 200,
	"HSN CODE": 110,
	"CALCULATED DISCOUNT": 150,
	"CALCULATED NET AMT": 150,
	"BILLING VALUE": 130,
	"TAXABLE VALUE": 130,
};

const STATUS_COLORS = {
	applied: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
	no_offer: { color: "#475467", bg: "#F2F4F7", border: "#E4E7EC" },
	default: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
};

const formatHeader = (column) =>
	String(column || "")
		.replace(/_/g, " ")
		.toUpperCase();

const formatNumber = (value) => {
	if (value === null || value === undefined || value === "") return "";
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return String(value);
	return numeric.toLocaleString("en-IN", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});
};

function StatusCell({ value }) {
	const text = String(value || "").trim();
	if (!text) return null;

	const key = text.toLowerCase();
	const tone =
		STATUS_COLORS[key] ||
		(key.includes("applied") ? STATUS_COLORS.applied : STATUS_COLORS.default);

	return (
		<Chip
			label={text.replace(/_/g, " ")}
			size="small"
			sx={{
				height: 22,
				fontSize: "11px",
				fontWeight: 600,
				color: tone.color,
				bgcolor: tone.bg,
				border: `1px solid ${tone.border}`,
			}}
		/>
	);
}

function TableLoadingSkeleton({ message }) {
	const columnWidths = ["18%", "12%", "14%", "10%", "16%", "11%", "19%"];

	return (
		<Box
			sx={{
				border: "1px solid #eee",
				borderRadius: 2,
				overflow: "hidden",
				bgcolor: "#fff",
			}}
		>
			<LinearProgress sx={{ height: 3 }} />
			<Stack
				direction="row"
				alignItems="center"
				spacing={1.5}
				sx={{ px: 2, py: 1.25 }}
			>
				<CircularProgress size={18} />
				<Typography variant="body2" color="text.secondary">
					{message}
				</Typography>
			</Stack>
			<Stack
				direction="row"
				spacing={1.5}
				sx={{ px: 2, py: 1.25, bgcolor: "#fafafa", borderTop: "1px solid #eee" }}
			>
				{columnWidths.map((width) => (
					<Skeleton key={width} variant="rounded" width={width} height={14} />
				))}
			</Stack>
			{Array.from({ length: 10 }).map((_, rowIndex) => (
				<Stack
					key={rowIndex}
					direction="row"
					spacing={1.5}
					alignItems="center"
					sx={{
						px: 2,
						height: 40,
						borderTop: "1px solid #f0f0f0",
					}}
				>
					{columnWidths.map((width) => (
						<Skeleton
							key={`${rowIndex}-${width}`}
							variant="rounded"
							width={width}
							height={12}
						/>
					))}
				</Stack>
			))}
		</Box>
	);
}

function ActionLoadingOverlay({ message }) {
	return (
		<Box
			sx={{
				position: "absolute",
				inset: 0,
				zIndex: 2,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 1.25,
				bgcolor: "rgba(255,255,255,0.78)",
				backdropFilter: "blur(1px)",
				borderRadius: 2,
			}}
		>
			<CircularProgress size={28} />
			<Typography variant="body2" color="text.secondary" fontWeight={600}>
				{message}
			</Typography>
			<Box sx={{ width: 180 }}>
				<LinearProgress />
			</Box>
		</Box>
	);
}

function PaymentWorkingDashboard() {
	const { setSnackBar, setSnackBarOpen } = GlobalState();
	const [fromDate, setFromDate] = useState(dayjs().subtract(30, "day"));
	const [toDate, setToDate] = useState(dayjs());
	const [selectedReportKey, setSelectedReportKey] = useState(DEFAULT_REPORT_KEY);
	const [downloading, setDownloading] = useState(false);
	const [error, setError] = useState("");

	const [previewColumns, setPreviewColumns] = useState([]);
	const [sortableColumns, setSortableColumns] = useState([]);
	const [previewRows, setPreviewRows] = useState([]);
	const [totalRows, setTotalRows] = useState(0);
	const [loadingPreview, setLoadingPreview] = useState(false);
	const [previewError, setPreviewError] = useState("");
	const [hasLoadedPreview, setHasLoadedPreview] = useState(false);
	const [loadedReportKey, setLoadedReportKey] = useState("");
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 25,
	});
	const [sortModel, setSortModel] = useState([]);
	const [tableSearch, setTableSearch] = useState("");

	const selectedReport = useMemo(
		() =>
			DOWNLOAD_REPORTS.find((report) => report.key === selectedReportKey) ||
			DOWNLOAD_REPORTS[0],
		[selectedReportKey]
	);

	const rangeError = useMemo(() => {
		if (!fromDate?.isValid() || !toDate?.isValid()) {
			return "Select both from and to dates.";
		}
		if (toDate.isBefore(fromDate, "day")) {
			return "To date cannot be before from date.";
		}
		return "";
	}, [fromDate, toDate]);

	const canDownload = !rangeError && !downloading && !loadingPreview;
	const isBusy = loadingPreview || downloading;

	const downloadFileName = useMemo(() => {
		if (!fromDate?.isValid() || !toDate?.isValid()) {
			return "";
		}
		const startDate = fromDate.format("YYYY-MM-DD");
		const endDate = toDate.format("YYYY-MM-DD");
		return `${selectedReport.filePrefix}_${startDate}_to_${endDate}.${selectedReport.fileExtension}`;
	}, [fromDate, toDate, selectedReport]);

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({
			...prev,
			message,
			severity,
		}));
	};

	const fetchPreview = useCallback(async () => {
		if (rangeError) {
			setPreviewError(rangeError);
			return;
		}

		try {
			setLoadingPreview(true);
			setPreviewError("");

			const activeSort = sortModel[0];
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/payment-working/preview",
				{
					params: {
						start_date: fromDate.format("YYYY-MM-DD"),
						end_date: toDate.format("YYYY-MM-DD"),
						report_type: selectedReport.reportType,
						limit: paginationModel.pageSize,
						offset: paginationModel.page * paginationModel.pageSize,
						...(activeSort?.field
							? {
									sort_by: activeSort.field,
									sort_dir: activeSort.sort === "desc" ? "desc" : "asc",
							  }
							: {}),
					},
				}
			);

			const columns = res.data?.columns || [];
			const rows = (res.data?.rows || []).map((row, index) => ({
				id: `${paginationModel.page}-${index}`,
				...row,
			}));

			setPreviewColumns(columns);
			setSortableColumns(res.data?.sortable_columns || columns);
			setPreviewRows(rows);
			setTotalRows(Number(res.data?.total_rows) || 0);
			setLoadedReportKey(selectedReport.key);
			setHasLoadedPreview(true);
		} catch (fetchError) {
			const message =
				fetchError?.response?.data?.message ||
				fetchError?.message ||
				"Failed to load payment working data";
			setPreviewError(message);
			setPreviewRows([]);
			setTotalRows(0);
			setLoadedReportKey(selectedReport.key);
			setHasLoadedPreview(true);
		} finally {
			setLoadingPreview(false);
		}
	}, [fromDate, toDate, paginationModel, rangeError, selectedReport, sortModel]);

	// Reload whenever the page or sort changes, but only after the first
	// explicit load. Sorting runs server-side so it spans the whole range.
	useEffect(() => {
		if (!hasLoadedPreview) return;
		// A pending report switch is handled by the effect below.
		if (loadedReportKey !== selectedReport.key) return;
		fetchPreview();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize, sortModel]);

	// Switching report changes the column shape — reload page 1 for the new report.
	useEffect(() => {
		if (!hasLoadedPreview) return;
		if (loadedReportKey === selectedReport.key) return;
		if (paginationModel.page !== 0) {
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			return;
		}
		fetchPreview();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedReport.key, loadedReportKey, hasLoadedPreview]);

	const sortableColumnSet = useMemo(
		() => new Set(sortableColumns),
		[sortableColumns]
	);

	const gridColumns = useMemo(
		() =>
			previewColumns.map((column) => {
				const isNumeric = !TEXT_COLUMNS.has(column);
				const isStatus =
					column === "discount_status" || column === "offer_type";

				return {
					field: column,
					headerName: formatHeader(column),
					width: WIDE_COLUMNS[column] || (isNumeric ? 115 : 130),
					align: isNumeric ? "right" : "left",
					headerAlign: isNumeric ? "right" : "left",
					sortable: sortableColumnSet.has(column),
					...(isNumeric
						? { valueFormatter: (value) => formatNumber(value) }
						: {}),
					...(isStatus
						? { renderCell: (params) => <StatusCell value={params.value} /> }
						: {}),
				};
			}),
		[previewColumns, sortableColumnSet]
	);

	// Sorting is server-side, so a new sort must restart from the first page.
	const handleSortModelChange = useCallback((nextSortModel) => {
		setSortModel(nextSortModel);
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	}, []);

	const filteredRows = useMemo(() => {
		const query = tableSearch.trim().toLowerCase();
		if (!query) return previewRows;
		return previewRows.filter((row) =>
			previewColumns.some((column) =>
				String(row[column] ?? "")
					.toLowerCase()
					.includes(query)
			)
		);
	}, [previewRows, previewColumns, tableSearch]);

	const handleDownload = async () => {
		if (rangeError) {
			setError(rangeError);
			return;
		}

		const report = selectedReport;
		if (!report) {
			return;
		}

		try {
			setDownloading(true);
			setError("");

			const startDate = fromDate.format("YYYY-MM-DD");
			const endDate = toDate.format("YYYY-MM-DD");
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/payment-working/download",
				{
					params: {
						start_date: startDate,
						end_date: endDate,
						report_type: report.reportType,
					},
					responseType: "blob",
					// Billing Excel can take several minutes for large date ranges.
					timeout: 15 * 60 * 1000,
				}
			);

			const contentType = res.headers?.["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await res.data.text();
				const errorData = JSON.parse(text);
				throw new Error(errorData.message || "Failed to download file");
			}

			const mimeType = report.mimeType || "text/csv;charset=utf-8;";
			const blob = new Blob([res.data], { type: mimeType });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", downloadFileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			showSnack(report.successMessage, "success");
		} catch (downloadError) {
			let message = "Failed to download file";
			const data = downloadError?.response?.data;
			if (data instanceof Blob) {
				try {
					const text = await data.text();
					const parsed = JSON.parse(text);
					message = parsed.message || message;
				} catch (_parseError) {
					message = downloadError?.message || message;
				}
			} else if (data?.message) {
				message = data.message;
			} else if (downloadError?.message) {
				message = downloadError.message;
			}
			setError(message);
			showSnack(message, "error");
		} finally {
			setDownloading(false);
		}
	};

	const handleViewData = () => {
		setLoadingPreview(true);
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		fetchPreview();
	};

	return (
		<Stack spacing={2.5}>
			<SectionCard title="Download reports" sx={{ mb: 0 }}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack
						direction="row"
						spacing={1.25}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
						sx={{
							width: "100%",
							pt: 0.5,
							rowGap: 1.25,
						}}
					>
						<DatePicker
							label="From date"
							format="DD/MM/YY"
							value={fromDate}
							maxDate={dayjs()}
							onChange={(value) => {
								const nextFrom = value || null;
								setFromDate(nextFrom);
								setToDate((prevTo) =>
									prevTo?.isValid() &&
									nextFrom?.isValid() &&
									prevTo.isBefore(nextFrom, "day")
										? nextFrom
										: prevTo
								);
								setError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<DatePicker
							label="To date"
							format="DD/MM/YY"
							value={toDate}
							minDate={fromDate?.isValid() ? fromDate : undefined}
							maxDate={dayjs()}
							disabled={!fromDate?.isValid()}
							onChange={(value) => {
								setToDate(value || null);
								setError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<TextField
							select
							label="Report"
							size="small"
							value={selectedReportKey}
							onChange={(event) => {
								// Reports expose different columns, so any active sort
								// on the old column shape no longer applies.
								setSortModel([]);
								setSelectedReportKey(event.target.value);
							}}
							sx={{
								width: { xs: "100%", sm: 260 },
								minWidth: { sm: 220 },
								...compactInputSx,
							}}
						>
							{DOWNLOAD_REPORTS.map((report) => (
								<MenuItem key={report.key} value={report.key}>
									{report.label}
								</MenuItem>
							))}
						</TextField>
						<Button
							variant="outlined"
							startIcon={
								loadingPreview ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<RefreshOutlinedIcon />
								)
							}
							onClick={handleViewData}
							disabled={Boolean(rangeError) || isBusy}
							sx={{ height: 40, px: 2, fontWeight: 600, whiteSpace: "nowrap" }}
						>
							{loadingPreview ? "Loading..." : "View Data"}
						</Button>
						<Button
							variant="contained"
							startIcon={
								downloading ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<DownloadOutlinedIcon />
								)
							}
							onClick={handleDownload}
							disabled={!canDownload || loadingPreview}
							sx={{
								...primaryButtonSx,
								height: 40,
								px: 2,
								whiteSpace: "nowrap",
							}}
						>
							{downloading ? "Downloading..." : "Download"}
						</Button>
					</Stack>
				</LocalizationProvider>

				{(error || rangeError) && (
					<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
						{error || rangeError}
					</Alert>
				)}
			</SectionCard>

			<SectionCard sx={{ mb: 0 }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.5}
					alignItems={{ xs: "stretch", sm: "center" }}
					justifyContent="space-between"
					sx={{ mb: 2 }}
				>
					<Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: 1.5,
								display: "grid",
								placeItems: "center",
								bgcolor: "rgba(33, 33, 33, 0.06)",
								color: "#212121",
								flexShrink: 0,
							}}
						>
							<TableChartOutlinedIcon fontSize="small" />
						</Box>
						<Box minWidth={0}>
							<Typography variant="h6" fontWeight={700}>
								{selectedReport.label} Data
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{loadingPreview
									? "Loading payment working data..."
									: downloading
									? "Preparing download..."
									: hasLoadedPreview
									? `${totalRows.toLocaleString("en-IN")} rows · ${previewColumns.length} columns · ${fromDate?.format("DD MMM YY")} - ${toDate?.format("DD MMM YY")}`
									: "Pick a date range and click View Data"}
							</Typography>
						</Box>
					</Stack>

					<TextField
						size="small"
						placeholder="Search table..."
						value={tableSearch}
						onChange={(event) => setTableSearch(event.target.value)}
						disabled={!hasLoadedPreview || isBusy}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon sx={{ fontSize: 20 }} />
								</InputAdornment>
							),
						}}
						sx={{
							width: { xs: "100%", sm: 300 },
							...compactInputSx,
						}}
					/>
				</Stack>

				{previewError ? (
					<Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
						{previewError}
					</Alert>
				) : null}

				{(loadingPreview && !hasLoadedPreview) || (downloading && !hasLoadedPreview) ? (
					<TableLoadingSkeleton
						message={
							downloading
								? "Preparing download..."
								: "Loading payment working data..."
						}
					/>
				) : hasLoadedPreview ? (
					<Box sx={{ width: "100%", height: 560, position: "relative" }}>
						<DataGrid
							rows={filteredRows}
							columns={gridColumns}
							loading={loadingPreview}
							rowHeight={40}
							columnHeaderHeight={44}
							disableRowSelectionOnClick
							disableColumnMenu
							paginationMode="server"
							rowCount={totalRows}
							paginationModel={paginationModel}
							onPaginationModelChange={setPaginationModel}
							pageSizeOptions={[25, 50, 100]}
							sortingMode="server"
							sortModel={sortModel}
							onSortModelChange={handleSortModelChange}
							sx={{
								border: "1px solid #eee",
								borderRadius: 2,
								"& .MuiDataGrid-columnHeaders": {
									bgcolor: "#fafafa",
								},
								"& .MuiDataGrid-columnHeaderTitle": {
									fontSize: "11px",
									fontWeight: 700,
									letterSpacing: 0.3,
								},
								"& .MuiDataGrid-cell": {
									fontSize: "12.5px",
									display: "flex",
									alignItems: "center",
								},
							}}
						/>
						{downloading ? (
							<ActionLoadingOverlay message="Preparing download..." />
						) : null}
					</Box>
				) : (
					<Box
						sx={{
							py: 5,
							textAlign: "center",
							border: "1px dashed #e0e0e0",
							borderRadius: 2,
							bgcolor: "#fafafa",
						}}
					>
						<TableChartOutlinedIcon sx={{ fontSize: 36, color: "#bdbdbd" }} />
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							No data loaded yet. Choose a date range and click{" "}
							<strong>View Data</strong>.
						</Typography>
					</Box>
				)}
			</SectionCard>
		</Stack>
	);
}

export default PaymentWorkingDashboard;
